import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Typography
} from '@mui/material'
import { InfoOutlined, Widgets } from '@mui/icons-material'
import { ActionButton, Input } from '@emerald-works-nova/components'
import React from 'react'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useDispatch, useSelector } from 'react-redux'
import { UserListSlice } from '../../../reducers'
import { BannerStatic } from '../../../components'
import { useForm } from 'react-hook-form'

const SuspendDialog = ({ open, onClose }) => {
  const { subscription } = useSelector(UserListSlice.selectors.selectUser)

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset
  } = useForm({ mode: 'onChange', defaultValues: { note: '', cycles: 1 } })

  const dispatch = useDispatch()

  const [updateSubscription] = useEvent([
    {
      eventName: 'subscriptionUpdate',
      onSuccess: payload => {
        dispatch(UserListSlice.actions.setUserSubscription(payload))
        BannerStatic.show({ label: 'User subscription suspended' })
        onClose()
      },
      onError: e => {
        console.error(
          "Error occured while attempting to suspend user's subscription",
          e
        )
        BannerStatic.show({
          label:
            e.message ||
            "Error occured while attempting to suspend user's subscription",
          isError: true
        })
      }
    }
  ])

  React.useEffect(() => () => open && reset(), [open, reset])

  const loading = updateSubscription.isWorking

  const handleSuspend = data => {
    updateSubscription.trigger({
      subscriptionId: subscription.uuid,
      suspension: data
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ style: { padding: 16 } }}
      data-test='suspend-subscription-dialog'
    >
      <DialogTitle id='suspend-dialog-title'>Suspend Subscription</DialogTitle>
      <DialogContent>
        <Divider variant='fullWidth' />
      </DialogContent>
      <DialogContent>
        <DialogContentText variant='subtitle1'>
          Some things to keep in mind
        </DialogContentText>
        <DialogContentText
          component='div'
          variant='body2'
          style={{
            display: 'flex',
            direction: 'row nowrap',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <Widgets />
          This user's data will be kept, but they won't access premium features
          as long as they're suspended.
        </DialogContentText>
        <DialogContentText
          component='div'
          variant='body2'
          style={{
            display: 'flex',
            direction: 'row nowrap',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <InfoOutlined />
          Suspended users can be restored as long as they're not deleted.
        </DialogContentText>
      </DialogContent>
      <DialogContent>
        <Grid container direction='column' spacing={2}>
          <Grid item>
            <Typography variant='body2'>
              Number of billing cycles to suspend
            </Typography>
          </Grid>
          <Grid item>
            <Input
              name='cycles'
              type='number'
              size='small'
              control={control}
              disabled={loading}
              InputProps={{
                inputProps: {
                  min: 1
                }
              }}
              rules={{
                required: 'Number of cycles is required'
              }}
              showErrors
              valid={!errors.cycles}
              error={errors?.cycles?.message}
              data-test='suspend-cycles-input'
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogContent>
        <Grid container direction='column' spacing={2}>
          <Grid item>
            <Typography variant='body2'>Notes</Typography>
          </Grid>
          <Grid item>
            <Input
              name='note'
              size='small'
              control={control}
              disabled={loading}
              multiline
              fullWidth
              rows={3}
              placeholder='Write here'
              data-test='suspend-note-input'
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ActionButton
          noBorder
          onClick={onClose}
          disabled={loading}
          dataTest='cancel-button'
        >
          Cancel
        </ActionButton>
        <ActionButton
          noBorder
          onClick={handleSubmit(handleSuspend)}
          isWorking={loading}
          dataTest='suspend-button'
        >
          Suspend
        </ActionButton>
      </DialogActions>
    </Dialog>
  )
}

export default SuspendDialog
